import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useRealtimeEvent } from "../contexts/RealtimeContext";
import { partnerToast } from "../lib/realtimeToast";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { CreateEntryDialog } from "../components/CreateEntryDialog";
import { CreateListDialog } from "../components/CreateListDialog";
import { SortableEntryCard } from "../components/SortableEntryCard";
import { AuthImage } from "../components/AuthImage";
import { Plus, ChevronLeft, MoreHorizontal, Trash2, Pencil, Heart, Plane, Coffee, Mountain, Home, Camera, Trees, Star, Compass, Map, Sparkles, BookOpen, Flame, Anchor } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import EntryDetail from "./EntryDetail";
import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";

const ICON_MAP = { Plane, Heart, Coffee, Mountain, Home, Camera, Trees, Star, Compass, Map, Sparkles, BookOpen, Flame, Anchor };
const STATUS_FILTERS = [
  { value: "all", label: "Alle" },
  { value: "idea", label: "Ideen" },
  { value: "planned", label: "Geplant" },
  { value: "booked", label: "Gebucht" },
  { value: "experienced", label: "Erlebt" },
  { value: "repeat", label: "Wiederkehrend" },
];

export default function ListView() {
  const { listId } = useParams();
  const { user, partner } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editList, setEditList] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const load = async () => {
    try {
      const [l, e] = await Promise.all([
        api.get(`/lists/${listId}`),
        api.get(`/entries?list_id=${listId}`),
      ]);
      setList(l.data);
      setEntries(e.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [listId]);

  // Real-time: react to events touching this list
  useRealtimeEvent(
    ["list:updated", "list:deleted", "entry:created", "entry:updated", "entry:deleted", "entry:vote", "entry:reaction", "entry:comment", "entries:reordered"],
    (data, actorId, event) => {
      if (data?.list_id && data.list_id !== listId && data?.id !== listId) return;
      if (event === "list:deleted" && data?.id === listId) {
        toast(`${partner?.name || "Partner:in"} hat diese Liste gelöscht`);
        navigate("/dashboard");
        return;
      }
      if (actorId && actorId !== user?.id) {
        partnerToast(event, partner?.name);
      }
      load();
    }
  );

  const Icon = list ? (ICON_MAP[list.icon] || Heart) : Heart;
  const filtered = filter === "all" ? entries : entries.filter((e) => e.status === filter);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = entries.findIndex((e) => e.id === active.id);
    const newIndex = entries.findIndex((e) => e.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newEntries = arrayMove(entries, oldIndex, newIndex);
    setEntries(newEntries);
    try {
      await api.post("/entries/reorder", {
        list_id: listId,
        ordered_ids: newEntries.map((e) => e.id),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      load();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Liste wirklich löschen? Alle Einträge gehen verloren.")) return;
    try {
      await api.delete(`/lists/${listId}`);
      toast.success("Liste gelöscht");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!list) return <Layout><div className="py-24 text-center text-text_secondary">Lade …</div></Layout>;

  return (
    <Layout>
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-text_secondary hover:text-ink mb-8" data-testid="back-to-dashboard">
        <ChevronLeft size={14} /> Zurück
      </Link>

      {list.cover_image && (
        <div className="relative aspect-[21/9] sm:aspect-[3/1] w-full rounded-3xl overflow-hidden mb-8 bg-bone" data-testid="list-cover-banner">
          <AuthImage fileId={list.cover_image} size="medium" className="absolute inset-0 w-full h-full object-cover" alt={list.title} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0" />
        </div>
      )}

      <header className="mb-12 flex flex-col sm:flex-row gap-6 sm:items-end justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: list.color }}>
            <Icon size={22} strokeWidth={1.6} className="text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-text_secondary">Liste</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink leading-tight tracking-tight">{list.title}</h1>
            {list.description && <p className="text-text_secondary mt-2 max-w-xl">{list.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setOpen(true)} className="rounded-full bg-ink text-alabaster hover:bg-ink/90" data-testid="add-entry-button">
            <Plus size={14} className="mr-1.5" /> Eintrag
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="list-options-button"><MoreHorizontal size={16} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditList(true)} data-testid="list-edit-option"><Pencil size={14} className="mr-2" /> Bearbeiten</DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-terracotta" data-testid="list-delete-option"><Trash2 size={14} className="mr-2" /> Löschen</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2 mb-8 pb-2 overflow-x-auto scrollbar-hide">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`filter-${f.value}`}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
              filter === f.value ? "bg-ink text-alabaster" : "bg-transparent text-text_secondary border border-border hover:border-ink hover:text-ink"
            }`}
          >
            {f.label} {filter === f.value ? "" : `· ${f.value === "all" ? entries.length : entries.filter((e) => e.status === f.value).length}`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-text_secondary">
          <Sparkles size={22} className="mx-auto mb-4 opacity-50" />
          <p className="font-serif text-2xl text-ink">Noch nichts hier.</p>
          <p className="mt-2">Was wäre der erste Eintrag, den ihr hinzufügen wollt?</p>
        </div>
      ) : (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filtered.map((e) => e.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 stagger" data-testid="entries-grid">
              {filtered.map((e) => (
                <SortableEntryCard
                  key={e.id}
                  entry={e}
                  currentUserId={user?.id}
                  onClick={() => setSelectedEntryId(e.id)}
                  disabled={filter !== "all"}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <CreateEntryDialog open={open} onOpenChange={setOpen} listId={listId} onCreated={() => load()} />
      <CreateListDialog open={editList} onOpenChange={setEditList} initial={list} onCreated={() => load()} />

      {selectedEntryId && (
        <EntryDetail
          entryId={selectedEntryId}
          onClose={() => setSelectedEntryId(null)}
          onChanged={() => load()}
        />
      )}
    </Layout>
  );
}
